import { useCallback, useEffect, useState } from "react";
import { Star, ClipboardCheck, AlertTriangle, Phone } from "lucide-react";
import { api, telLink } from "@/lib/api";
import { can } from "@/lib/rbac";
import { useAuth } from "@/context/AuthContext";
import { PageHead, EmptyState } from "@/components/Bits";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { FeedbackForm, StarRating } from "@/components/FeedbackForm";

const PendingTab = () => {
  const [rows, setRows] = useState([]);
  const [active, setActive] = useState(null);
  const load = useCallback(() => {
    api.get("/feedback/pending").then(({ data }) => setRows(data)).catch(() => setRows([]));
  }, []);
  useEffect(() => { load(); }, [load]);

  return (
    <Card className="border-brand-line shadow-none">
      <CardContent className="overflow-x-auto p-0">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Customer</TableHead>
              <TableHead>Phone</TableHead>
              <TableHead>Vehicle</TableHead>
              <TableHead>Studio</TableHead>
              <TableHead>Delivered</TableHead>
              <TableHead>Status</TableHead>
              <TableHead />
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={7} className="p-0">
                  <EmptyState
                    icon={ClipboardCheck}
                    title="No feedback pending"
                    hint="Delivered jobs appear here until feedback is collected."
                    testid="feedback-pending-empty"
                  />
                </TableCell>
              </TableRow>
            )}
            {rows.map((r) => (
              <TableRow key={r.job_id} data-testid={`feedback-pending-${r.job_id}`}>
                <TableCell className="font-medium text-brand-ink">{r.customer_name}</TableCell>
                <TableCell className="text-slate-500" data-testid={`feedback-pending-phone-${r.job_id}`}>
                  {r.customer_phone ? (
                    <a href={telLink(r.customer_phone)} className="hover:text-crimson-600">{r.customer_phone}</a>
                  ) : "—"}
                </TableCell>
                <TableCell className="text-slate-500">{r.vehicle || "—"}</TableCell>
                <TableCell className="text-slate-500">{r.studio_name}</TableCell>
                <TableCell className="text-slate-500">{r.delivery_date}</TableCell>
                <TableCell>
                  {r.overdue ? (
                    <Badge className="border-0 bg-crimson-100 text-crimson-700" data-testid={`feedback-overdue-${r.job_id}`}>
                      <AlertTriangle className="mr-1 h-3 w-3" /> Overdue · {r.days_since}d
                    </Badge>
                  ) : (
                    <Badge className="border-0 bg-amber-100 text-amber-800">Pending · {r.days_since}d</Badge>
                  )}
                </TableCell>
                <TableCell>
                  <div className="flex flex-wrap items-center gap-2">
                    {r.customer_phone && (
                      <Button
                        size="sm"
                        variant="outline"
                        className="min-h-[36px]"
                        data-testid={`feedback-call-${r.job_id}`}
                        onClick={() => { window.location.href = telLink(r.customer_phone); }}
                      >
                        <Phone className="mr-1 h-3.5 w-3.5" /> Call
                      </Button>
                    )}
                    <Button size="sm" data-testid={`collect-feedback-${r.job_id}`} onClick={() => setActive(r)}>
                      Collect Feedback
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>

      <Dialog open={!!active} onOpenChange={(v) => !v && setActive(null)}>
        <DialogContent className="sm:max-w-md" data-testid="collect-feedback-dialog">
          <DialogHeader>
            <DialogTitle>
              Collect feedback — {active?.customer_name}
            </DialogTitle>
          </DialogHeader>
          {active && (
            <FeedbackForm
              jobId={active.job_id}
              onSaved={() => {
                setActive(null);
                load();
              }}
            />
          )}
        </DialogContent>
      </Dialog>
    </Card>
  );
};

const CompletedTab = () => {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [view, setView] = useState(null);
  useEffect(() => {
    api.get("/feedback/completed").then(({ data }) => setRows(data)).catch(() => setRows([]));
  }, []);
  const filtered = rows.filter((r) => {
    const s = q.toLowerCase();
    return !s || [r.customer_name, r.customer_phone, r.vehicle, r.studio_name].some((v) => (v || "").toLowerCase().includes(s));
  });
  return (
    <Card className="border-brand-line shadow-none">
      <CardContent className="p-4">
        <Input
          className="mb-3 max-w-sm"
          data-testid="feedback-completed-search"
          placeholder="Search by customer, phone, car or studio…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
        />
        <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Customer</TableHead>
              <TableHead>Phone</TableHead>
              <TableHead>Vehicle</TableHead>
              <TableHead>Studio</TableHead>
              <TableHead>Delivered</TableHead>
              <TableHead>Feedback on</TableHead>
              <TableHead>Overall</TableHead>
              <TableHead />
            </TableRow>
          </TableHeader>
          <TableBody>
            {filtered.length === 0 && (
              <TableRow>
                <TableCell colSpan={8} className="p-0">
                  <EmptyState
                    icon={Star}
                    title="No completed feedback yet"
                    hint="Feedback you collect appears here permanently."
                    testid="feedback-completed-empty"
                  />
                </TableCell>
              </TableRow>
            )}
            {filtered.map((r) => (
              <TableRow key={r.job_id} data-testid={`feedback-completed-${r.job_id}`}>
                <TableCell className="font-medium text-brand-ink">{r.customer_name}</TableCell>
                <TableCell className="text-slate-500" data-testid={`feedback-completed-phone-${r.job_id}`}>
                  {r.customer_phone ? (
                    <a href={telLink(r.customer_phone)} className="hover:text-crimson-600">{r.customer_phone}</a>
                  ) : "—"}
                </TableCell>
                <TableCell className="text-slate-500">{r.vehicle || "—"}</TableCell>
                <TableCell className="text-slate-500">{r.studio_name}</TableCell>
                <TableCell className="text-slate-500">{r.delivery_date || "—"}</TableCell>
                <TableCell className="text-slate-500">{r.submitted_date || "—"}</TableCell>
                <TableCell>
                  <span className="font-semibold text-amber-600">{r.overall}</span>
                  <span className="text-slate-400"> / 5</span>
                </TableCell>
                <TableCell>
                  <Button size="sm" variant="outline" data-testid={`view-feedback-${r.job_id}`} onClick={() => setView(r)}>
                    View
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        </div>
      </CardContent>

      <Dialog open={!!view} onOpenChange={(v) => !v && setView(null)}>
        <DialogContent className="sm:max-w-md" data-testid="view-feedback-dialog">
          <DialogHeader>
            <DialogTitle>Feedback — {view?.customer_name}</DialogTitle>
          </DialogHeader>
          {view && (
            <div className="space-y-3">
              <p className="text-sm text-slate-500">{view.vehicle} · {view.studio_name}</p>
              <div className="grid grid-cols-3 gap-3">
                {[["Overall", view.overall], ["Quality", view.quality], ["Staff", view.staff_behaviour]].map(([l, v]) => (
                  <div key={l}>
                    <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">{l}</p>
                    <StarRating value={v} readOnly />
                  </div>
                ))}
              </div>
              <p className="text-sm">
                <span className="text-xs font-semibold uppercase tracking-wide text-slate-400">Would recommend: </span>
                <span className={view.recommend ? "text-emerald-600" : "text-crimson-600"}>
                  {view.recommend ? "Yes" : "No"}
                </span>
              </p>
              {view.comments && <p className="text-sm text-slate-600">"{view.comments}"</p>}
              <p className="text-xs text-slate-400">
                Collected by {view.collected_by_name} · submitted {view.submitted_date}
              </p>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </Card>
  );
};

const ScoresTab = () => {
  const [data, setData] = useState({ studios: [], total: 0 });
  useEffect(() => {
    api.get("/reports/feedback").then(({ data }) => setData(data)).catch(() => {});
  }, []);
  return (
    <Card className="border-brand-line shadow-none">
      <CardContent className="overflow-x-auto p-0">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Studio</TableHead>
              <TableHead>Responses</TableHead>
              <TableHead>Overall</TableHead>
              <TableHead>Quality</TableHead>
              <TableHead>Staff</TableHead>
              <TableHead>Recommend</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {data.studios.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} className="p-0">
                  <EmptyState
                    icon={Star}
                    title="No feedback collected yet"
                    hint="Aggregate scores appear once CRM starts collecting feedback."
                    testid="feedback-scores-empty"
                  />
                </TableCell>
              </TableRow>
            )}
            {data.studios.map((s) => (
              <TableRow key={s.studio_id} data-testid={`feedback-score-${s.studio_id}`}>
                <TableCell className="font-medium text-brand-ink">{s.studio_name}</TableCell>
                <TableCell>{s.count}</TableCell>
                <TableCell>
                  <span className="font-semibold text-amber-600">{s.avg_overall}</span>
                  <span className="text-slate-400"> / 5</span>
                </TableCell>
                <TableCell>{s.avg_quality} / 5</TableCell>
                <TableCell>{s.avg_staff_behaviour} / 5</TableCell>
                <TableCell>
                  <span className={s.recommend_pct >= 70 ? "text-emerald-600" : "text-slate-600"}>
                    {s.recommend_pct}%
                  </span>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
};

export default function Feedback() {
  const { user } = useAuth();
  const canCollect = can(user, "feedback:collect");
  return (
    <div className="p-4 sm:p-6 lg:p-8">
      <PageHead
        title="Customer Feedback"
        subtitle="Collect post-delivery feedback and track satisfaction scores across studios"
      />
      <Tabs defaultValue={canCollect ? "pending" : "scores"}>
        <TabsList>
          {canCollect && (
            <TabsTrigger value="pending" data-testid="tab-feedback-pending">
              Pending feedback
            </TabsTrigger>
          )}
          <TabsTrigger value="completed" data-testid="tab-feedback-completed">
            Completed feedback
          </TabsTrigger>
          <TabsTrigger value="scores" data-testid="tab-feedback-scores">
            Studio scores
          </TabsTrigger>
        </TabsList>
        {canCollect && (
          <TabsContent value="pending" className="mt-4">
            <PendingTab />
          </TabsContent>
        )}
        <TabsContent value="completed" className="mt-4">
          <CompletedTab />
        </TabsContent>
        <TabsContent value="scores" className="mt-4">
          <ScoresTab />
        </TabsContent>
      </Tabs>
    </div>
  );
}
